<script setup lang="ts">
import type { DesignModel } from '@forgd/contract/types'
import { useDesignConfirm } from '#design-tokenomics/composables/useDesignConfirm'
import { registerUnsavedWork } from '#design-tokenomics/composables/useDesignLeaveGuard'
import { useDesignQuickAdjust } from '#design-tokenomics/composables/useDesignQuickAdjust'
import { Button } from '@forgd/ui-kit/button'
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@forgd/ui-kit/sheet'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { klona } from 'klona'
import { isEqual } from 'ohash/utils'

const props = defineProps<{
  model: DesignModel
}>()

const emit = defineEmits<{
  save: [model: DesignModel]
}>()

const { isOpen, panel, size, close } = useDesignQuickAdjust()
const { confirm } = useDesignConfirm()

const working = ref<DesignModel>(klona(props.model))

watch(isOpen, (open) => {
  if (open) {
    working.value = klona(props.model)
  }
})

const isDirty = computed(() => !isEqual(working.value, props.model))

registerUnsavedWork(() => isOpen.value && isDirty.value)

function save() {
  emit('save', klona(toRaw(working.value)))
  close()
}

async function requestClose() {
  if (!isDirty.value) {
    close()
    return
  }

  const proceed = await confirm({
    title: 'Are you sure you want to leave without saving your latest changes?',
    description: 'All unsaved adjustments in this panel will be lost.',
    confirmLabel: 'Leave without saving',
    cancelLabel: 'Keep editing',
  })

  if (proceed) {
    close()
  }
}
</script>

<template>
  <Sheet :open="isOpen" :modal="false" @update:open="(open) => !open && requestClose()">
    <SheetContent side="right" :size="size" data-ui="Sheet">
      <SheetHeader>
        <SheetTitle class="text-sm font-semibold">
          {{ panel?.title }}
        </SheetTitle>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          icon-only
          aria-label="Close"
          data-testid="dt-quick-adjust-close"
          @click="requestClose"
        >
          <XMarkIcon />
        </Button>
      </SheetHeader>

      <SheetBody class="p-0">
        <div class="grow" :class="{ 'p-5': !panel?.flush }">
          <component :is="panel.component" v-if="panel" v-model="working" />
        </div>
      </SheetBody>

      <SheetFooter>
        <div class="flex items-center gap-4">
          <Button
            size="lg"
            class="w-58.75"
            :disabled="!isDirty"
            data-testid="dt-quick-adjust-save"
            @click="save"
          >
            Save
          </Button>
          <Button
            size="lg"
            variant="outline"
            class="w-58.75"
            data-testid="dt-quick-adjust-cancel"
            @click="requestClose"
          >
            Cancel
          </Button>
        </div>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
